/**
 * @file LabelCodeModeField.tsx
 * @description «Коды на этикетке: Оба / DataMatrix / Штрих» (phase19.22,
 *   W17-5c; ui-design §1).
 *
 * Сегменты-кнопки 56px с `aria-pressed`, а не нативное радио: тач-цель
 * склада. Значение по умолчанию приходит из настроек склада. «Штрих» несёт
 * только код фасовки — под кнопками предупреждение; мини-этикетке «штрих»
 * недоступен (там только DataMatrix) — это подписано, а форма сама шлёт
 * мини-строкам DataMatrix.
 */

import { TriangleAlert } from "lucide-react";

import { LABEL_TEXT } from "./label-texts";
import { LABEL_CODE_MODE_META, type LabelCodeMode } from "../../../lib/api-labels";
import { cn } from "@/lib/utils";

/** Порядок сегментов. */
const MODES: LabelCodeMode[] = ["BOTH", "DATAMATRIX_ONLY", "CODE128_ONLY"];

interface Props {
  value: LabelCodeMode;
  onChange: (value: LabelCodeMode) => void;
  /** В печати есть мини-этикетки — подписать, что им «штрих» не достанется. */
  hasMini: boolean;
}

export function LabelCodeModeField({ value, onChange, hasMini }: Props) {
  return (
    <div className="flex flex-col gap-2">
      <div role="group" aria-label="Коды на этикетке" className="grid grid-cols-3 gap-1.5">
        {MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            aria-pressed={value === mode}
            onClick={() => onChange(mode)}
            className={cn(
              "min-h-14 rounded-xl px-2 text-sm transition-colors",
              value === mode
                ? "bg-primary font-medium text-on-primary"
                : "bg-surface-low text-on-surface-variant hover:bg-accent",
            )}
          >
            {LABEL_CODE_MODE_META[mode].label}
          </button>
        ))}
      </div>
      {value === "CODE128_ONLY" ? (
        <p className="flex items-start gap-1.5 text-xs text-warning">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>
            {LABEL_TEXT.code128Warning}
            {hasMini ? ` ${LABEL_TEXT.miniCodeNote}` : ""}
          </span>
        </p>
      ) : null}
    </div>
  );
}
